import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useDebouncedValue, useIntersection } from '@mantine/hooks';
import { DragModal } from 'components/DragModal/DragModal';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import SkeletonBox from 'components/generic/SkeletonBox';
import { InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import {
  GetSectionListResult,
  SectionListItem,
  GetKeywordListResult,
  KeywordListItem,
} from 'providers/contexts/extensions/SiteTaxonomy';
import { assetTypesSet } from 'utility/types/cosmosTypes';

// ---------------------------------------------------------
// Types
// ---------------------------------------------------------

export type RuleType = 'assetType' | 'flags' | 'keywords' | 'sections';

export type RuleFormData = {
  name?: string;
  type: RuleType;
  values: string[];
};

type RuleWizardModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: RuleFormData) => void;
  initialData?: RuleFormData;
  isEdit?: boolean;
  usedRuleTypes?: RuleType[];
};

// Asset types available in the system
const ASSET_TYPES = Array.from(assetTypesSet);

const PAGE_SIZE = 50;

// ---------------------------------------------------------
// Flag item type (derived from editorial config)
// ---------------------------------------------------------

type FlagItem = {
  key: string;
  label: string;
};

// ---------------------------------------------------------
// Empty State Component
// ---------------------------------------------------------

const EmptyState = memo(({ message }: { message: string }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        m: 2,
        p: 3,
        textAlign: 'center',
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {message}
      </Typography>
    </Box>
  );
});
EmptyState.displayName = 'EmptyState';

// ---------------------------------------------------------
// Step Search Bar Component
// ---------------------------------------------------------

type StepSearchBarProps = {
  searchText: string;
  onSearchTextChange: (value: string) => void;
  selectedType: RuleType | null;
  onKeywordKeyDown?: (e: React.KeyboardEvent) => void;
  onClearSearch: () => void;
  onAddKeyword?: () => void;
  isFetching: boolean;
  isSearching: boolean;
  selectedCount: number;
  resultCountText: string;
  addDisabled?: boolean;
};

const StepSearchBar = memo(
  ({
    searchText,
    onSearchTextChange,
    selectedType,
    onKeywordKeyDown,
    onClearSearch,
    onAddKeyword,
    isFetching,
    isSearching,
    selectedCount,
    resultCountText,
    addDisabled,
  }: StepSearchBarProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const strSearch = t('blox.actions.search');
    const strAdd = t('blox.actions.add');
    const strSelected = t('blox.general.selected');
    const strEnterKeyword = t('blox.notifier.channel.rules.enter_keyword');

    return (
      <Box
        sx={{
          pb: 1,
          mb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <TextField
            value={searchText}
            onChange={(e) => onSearchTextChange(e.target.value)}
            onKeyDown={selectedType === 'keywords' ? onKeywordKeyDown : undefined}
            placeholder={
              selectedType === 'keywords' ? strEnterKeyword : `${strSearch}...`
            }
            size="small"
            fullWidth
            autoComplete="off"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: bloxValues.smallFontSizeRem,
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: bloxValues.iconSizeSmall }}
                      color="action"
                    />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    {isFetching && isSearching ? (
                      <CircularProgress size={16} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={onClearSearch}
                        edge="end"
                        aria-label="Clear search"
                      >
                        <ClearIcon sx={{ fontSize: bloxValues.iconSizeSmall }} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          {selectedType === 'keywords' && searchText.trim() && (
            <Button
              variant="outlined"
              size="small"
              onClick={onAddKeyword}
              disabled={addDisabled}
              disableElevation
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {strAdd}
            </Button>
          )}
          {selectedCount > 0 && (
            <Chip
              label={`${selectedCount} ${strSelected.toLowerCase()}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: bloxValues.fontWeightMedium,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            />
          )}
          {resultCountText && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {resultCountText}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  },
);
StepSearchBar.displayName = 'StepSearchBar';

// ---------------------------------------------------------
// Rule Wizard Step Component
// ---------------------------------------------------------

type RuleWizardStepProps = {
  activeStep: number;
  selectedType: RuleType | null;
  selectedValues: string[];
  onTypeSelect: (type: RuleType) => void;
  onValueToggle: (value: string) => void;
  onAddValue: (value: string) => void;
  onRemoveValue: (value: string) => void;
  ruleName: string;
  onRuleNameChange: (name: string) => void;
  open: boolean;
  usedRuleTypes?: RuleType[];
};

const RuleWizardStep = memo(
  ({
    activeStep,
    selectedType,
    selectedValues,
    onTypeSelect,
    onValueToggle,
    onAddValue,
    onRemoveValue,
    ruleName,
    onRuleNameChange,
    open,
    usedRuleTypes = [],
  }: RuleWizardStepProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();

    // Search state (colocated — only used within step content)
    const [searchText, setSearchText] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchText, 300);

    // Intersection observer for infinite scroll
    const { ref: ixRef, entry } = useIntersection({ root: null, threshold: 0.5 });

    // Reset search when step or type changes
    useEffect(() => {
      setSearchText('');
    }, [activeStep, selectedType]);

    const isSearching = Boolean(debouncedSearch);

    const strLoading = t('blox.general.loading');
    const strNoResults = t('blox.general.no_results');
    const strEndOfResults = t('blox.general.end_of_results');
    const strAssetType = t('blox.notifier.channel.rules.asset_type');
    const strFlags = t('blox.notifier.channel.rules.flags');
    const strKeywords = t('blox.notifier.channel.rules.keyword_tags');
    const strSections = t('blox.notifier.channel.rules.section_tags');
    const strRuleName = t('blox.notifier.channel.rules.rule_name');
    const strRuleNameHelper = t('blox.notifier.channel.rules.rule_name_helper');
    const strSelectedValues = t('blox.notifier.channel.rules.selected_values');
    const strNoFlags = t('blox.notifier.channel.rules.no_flags');
    const strNoSections = t('blox.notifier.channel.rules.no_sections');
    const strReviewRule = t('blox.notifier.channel.rules.review_rule');
    const strSelectTypeDesc = t('blox.notifier.channel.rules.select_type_desc');
    const strRuleType = t('blox.notifier.channel.rules.rule_type');

    const getAssetTypeLabel = useCallback(
      (type: string) => {
        const key = `blox.general.asset_types.${type}`;
        const translated = t(key);
        return translated;
      },
      [t],
    );

    const ruleTypeOptions = useMemo(
      () =>
        [
          { value: 'assetType' as RuleType, label: strAssetType },
          { value: 'flags' as RuleType, label: strFlags },
          { value: 'keywords' as RuleType, label: strKeywords },
          { value: 'sections' as RuleType, label: strSections },
        ].filter((option) => !usedRuleTypes.includes(option.value)),
      [strAssetType, strFlags, strKeywords, strSections, usedRuleTypes],
    );

    const showSearch =
      activeStep === 1 &&
      (selectedType === 'flags' ||
        selectedType === 'sections' ||
        selectedType === 'keywords');

    // ---------------------------------------------------
    // Fetch flags from editorial config (finite set, no pagination)
    // ---------------------------------------------------
    const { data: flagItems, isLoading: flagsLoading } = useQuery({
      queryKey: ['editorial/config/flags'],
      queryFn: async () => {
        const result = await dataProvider.getEditorialConfig();
        const flagsRecord = result?.data?.flags;
        if (!flagsRecord) return [] as FlagItem[];
        return Object.entries(flagsRecord).map(([key, def]) => ({
          key,
          label: def?.label || key,
        }));
      },
      enabled: open && selectedType === 'flags',
      staleTime: 5 * 60 * 1000,
    });

    // Client-side search filter for flags
    const filteredFlags = useMemo(() => {
      if (!flagItems) return [];
      if (!debouncedSearch) return flagItems;
      const lower = debouncedSearch.toLowerCase();
      return flagItems.filter(
        (f) =>
          f.key.toLowerCase().includes(lower) || f.label.toLowerCase().includes(lower),
      );
    }, [flagItems, debouncedSearch]);

    // ---------------------------------------------------
    // Fetch sections with infinite scroll + server search
    // ---------------------------------------------------
    const {
      data: sectionsData,
      fetchNextPage: fetchNextSections,
      hasNextPage: hasNextSections,
      isFetching: isFetchingSections,
      isFetchingNextPage: isFetchingNextSections,
      isLoading: sectionsLoading,
    } = useInfiniteQuery<
      GetSectionListResult,
      Error,
      InfiniteData<GetSectionListResult>,
      (string | undefined)[],
      number
    >({
      queryKey: ['core/section/list', 'rule-wizard', debouncedSearch || undefined],
      queryFn: async ({ pageParam }) => {
        return dataProvider.getSectionList({
          page: pageParam,
          limit: PAGE_SIZE,
          ...(debouncedSearch ? { query: debouncedSearch } : {}),
        });
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        const next = lastPage?.data?.next;
        return next ? Number(next) : undefined;
      },
      enabled: open && selectedType === 'sections',
      staleTime: 5 * 60 * 1000,
    });

    const allSections = useMemo<SectionListItem[]>(() => {
      return sectionsData?.pages?.flatMap((page) => page?.data?.items ?? []) ?? [];
    }, [sectionsData]);

    const sectionsTotalCount = useMemo(() => {
      const total = sectionsData?.pages?.[0]?.data?.total;
      return total ? Number(total) : 0;
    }, [sectionsData]);

    // ---------------------------------------------------
    // Fetch keywords with infinite scroll + server search
    // ---------------------------------------------------
    const {
      data: keywordsData,
      fetchNextPage: fetchNextKeywords,
      hasNextPage: hasNextKeywords,
      isFetching: isFetchingKeywords,
      isFetchingNextPage: isFetchingNextKeywords,
      isLoading: keywordsLoading,
    } = useInfiniteQuery<
      GetKeywordListResult,
      Error,
      InfiniteData<GetKeywordListResult>,
      (string | undefined)[],
      number
    >({
      queryKey: ['core/keyword/list', 'rule-wizard', debouncedSearch || undefined],
      queryFn: async ({ pageParam }) => {
        return dataProvider.getKeywordList({
          page: pageParam,
          limit: PAGE_SIZE,
          ...(debouncedSearch ? { query: debouncedSearch } : {}),
        });
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        const next = lastPage?.data?.next;
        return next ? Number(next) : undefined;
      },
      enabled: open && selectedType === 'keywords',
      staleTime: 5 * 60 * 1000,
    });

    const allKeywords = useMemo<KeywordListItem[]>(() => {
      return keywordsData?.pages?.flatMap((page) => page?.data?.items ?? []) ?? [];
    }, [keywordsData]);

    const keywordsTotalCount = useMemo(() => {
      const total = keywordsData?.pages?.[0]?.data?.total;
      return total ? Number(total) : 0;
    }, [keywordsData]);

    // Result count text for the search bar
    const resultCountText = useMemo(() => {
      if (selectedType === 'sections' && !sectionsLoading) {
        return hasNextSections
          ? `${allSections.length} / ${sectionsTotalCount}`
          : `${sectionsTotalCount}`;
      }
      if (selectedType === 'keywords' && !keywordsLoading) {
        return hasNextKeywords
          ? `${allKeywords.length} / ${keywordsTotalCount}`
          : `${keywordsTotalCount}`;
      }
      if (selectedType === 'flags' && !flagsLoading && flagItems) {
        return isSearching
          ? `${filteredFlags.length} / ${flagItems.length}`
          : `${flagItems.length}`;
      }
      return '';
    }, [
      selectedType,
      sectionsLoading,
      hasNextSections,
      allSections.length,
      sectionsTotalCount,
      keywordsLoading,
      hasNextKeywords,
      allKeywords.length,
      keywordsTotalCount,
      flagsLoading,
      flagItems,
      filteredFlags.length,
      isSearching,
    ]);

    // Handlers for search interaction
    const handleAddKeyword = useCallback(() => {
      const value = searchText.trim();
      if (value) {
        onAddValue(value);
        setSearchText('');
      }
    }, [searchText, onAddValue]);

    const handleKeywordKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleAddKeyword();
        }
      },
      [handleAddKeyword],
    );

    const handleClearSearch = useCallback(() => {
      setSearchText('');
    }, []);

    switch (activeStep) {
      // ===========================================================
      // Step 1: Choose Rule Type
      // ===========================================================
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 2,
                fontSize: bloxValues.smallFontSizeRem,
              }}
            >
              {strSelectTypeDesc}
            </Typography>
            <RadioGroup
              value={selectedType || ''}
              onChange={(e) => onTypeSelect(e.target.value as RuleType)}
            >
              {ruleTypeOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={
                    <Typography
                      variant="body2"
                      sx={{ fontSize: bloxValues.smallFontSizeRem }}
                    >
                      {option.label}
                    </Typography>
                  }
                  sx={{
                    mx: 0,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    '&:hover': { bgcolor: theme.palette.action.hover },
                    ...(selectedType === option.value && {
                      bgcolor: theme.palette.action.selected,
                    }),
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
        );

      // ===========================================================
      // Step 2: Select Criteria
      // ===========================================================
      case 1:
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              pt: 1,
            }}
          >
            {showSearch && (
              <StepSearchBar
                searchText={searchText}
                onSearchTextChange={setSearchText}
                selectedType={selectedType}
                onKeywordKeyDown={handleKeywordKeyDown}
                onClearSearch={handleClearSearch}
                onAddKeyword={handleAddKeyword}
                isFetching={isFetchingSections || isFetchingKeywords}
                isSearching={isSearching}
                selectedCount={selectedValues.length}
                resultCountText={resultCountText}
                addDisabled={selectedValues.includes(searchText.trim())}
              />
            )}

            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                minHeight: 0,
                scrollbarGutter: 'stable',
              }}
            >
              {/* --- Asset Types --- */}
              {selectedType === 'assetType' && (
                <FormGroup sx={{ py: 0.5 }}>
                  {ASSET_TYPES.map((type) => {
                    const isChecked = selectedValues.includes(type);
                    return (
                      <FormControlLabel
                        key={type}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => onValueToggle(type)}
                            size="small"
                            sx={{ p: 0.5 }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ fontSize: bloxValues.smallFontSizeRem }}
                          >
                            {getAssetTypeLabel(type)}
                          </Typography>
                        }
                        sx={{
                          mx: 0,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          '&:hover': { bgcolor: theme.palette.action.hover },
                          ...(isChecked && {
                            bgcolor: theme.palette.action.selected,
                          }),
                        }}
                      />
                    );
                  })}
                </FormGroup>
              )}

              {/* --- Flags (finite set, client-side search) --- */}
              {selectedType === 'flags' && (
                <>
                  {flagsLoading ? (
                    <Box sx={{ p: 2 }}>
                      <SkeletonBox
                        text={strLoading}
                        height={120}
                      />
                    </Box>
                  ) : filteredFlags.length > 0 ? (
                    <FormGroup sx={{ py: 0.5 }}>
                      {filteredFlags.map((flag) => {
                        const isChecked = selectedValues.includes(flag.key);
                        return (
                          <FormControlLabel
                            key={flag.key}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={() => onValueToggle(flag.key)}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ fontSize: bloxValues.smallFontSizeRem }}
                              >
                                {flag.label}
                              </Typography>
                            }
                            sx={{
                              mx: 0,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              '&:hover': { bgcolor: theme.palette.action.hover },
                              ...(isChecked && {
                                bgcolor: theme.palette.action.selected,
                              }),
                            }}
                          />
                        );
                      })}
                    </FormGroup>
                  ) : (
                    <EmptyState message={strNoFlags} />
                  )}
                </>
              )}

              {/* --- Keywords (infinite scroll + search + manual add) --- */}
              {selectedType === 'keywords' && (
                <Box sx={{ py: 0.5 }}>
                  {/* Selected keywords as chips */}
                  {selectedValues.length > 0 && (
                    <Box
                      sx={{
                        px: 1,
                        mb: 1,
                        pb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Stack
                        direction="row"
                        sx={{
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        {selectedValues.map((keyword) => (
                          <Chip
                            key={keyword}
                            label={keyword}
                            size="small"
                            variant="outlined"
                            onDelete={() => onRemoveValue(keyword)}
                            sx={{
                              '& .MuiChip-deleteIcon': {
                                '&:hover': { color: theme.palette.error.main },
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Keyword suggestions from API */}
                  {keywordsLoading ? (
                    <Box sx={{ p: 2 }}>
                      <SkeletonBox
                        text={strLoading}
                        height={120}
                      />
                    </Box>
                  ) : allKeywords.length > 0 ? (
                    <FormGroup sx={{ py: 0.5 }}>
                      {allKeywords.map((kw) => {
                        const value = kw.name || kw.path;
                        const isChecked = selectedValues.includes(value);
                        return (
                          <FormControlLabel
                            key={kw.id || kw.path}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={() => onValueToggle(value)}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ fontSize: bloxValues.smallFontSizeRem }}
                              >
                                {kw.name}
                              </Typography>
                            }
                            sx={{
                              mx: 0,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              '&:hover': { bgcolor: theme.palette.action.hover },
                              ...(isChecked && {
                                bgcolor: theme.palette.action.selected,
                              }),
                            }}
                          />
                        );
                      })}
                    </FormGroup>
                  ) : isSearching ? (
                    <EmptyState message={strNoResults} />
                  ) : null}

                  {/* Infinite scroll loader for keywords */}
                  <IntersectionLoader
                    hasNextPage={hasNextKeywords}
                    isFetching={isFetchingNextKeywords || isFetchingKeywords}
                    fetchNextPage={fetchNextKeywords}
                    ixRef={ixRef}
                    entry={entry}
                    endOfResultsMessage={
                      allKeywords.length > 0 ? strEndOfResults : undefined
                    }
                    compact
                    hideOnPagesEnd={allKeywords.length < PAGE_SIZE}
                  />
                </Box>
              )}

              {/* --- Sections (infinite scroll + search) --- */}
              {selectedType === 'sections' && (
                <Box sx={{ py: 0.5 }}>
                  {sectionsLoading ? (
                    <Box sx={{ p: 2 }}>
                      <SkeletonBox
                        text={strLoading}
                        height={120}
                      />
                    </Box>
                  ) : allSections.length > 0 ? (
                    <FormGroup sx={{ py: 0.5 }}>
                      {allSections.map((section) => {
                        const value = section.path || section.name || String(section.id);
                        const isChecked = selectedValues.includes(value);
                        return (
                          <FormControlLabel
                            key={section.id || section.path}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={() => onValueToggle(value)}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ fontSize: bloxValues.smallFontSizeRem }}
                              >
                                {section.path || section.name}
                              </Typography>
                            }
                            sx={{
                              mx: 0,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              '&:hover': { bgcolor: theme.palette.action.hover },
                              ...(isChecked && {
                                bgcolor: theme.palette.action.selected,
                              }),
                            }}
                          />
                        );
                      })}
                    </FormGroup>
                  ) : (
                    <EmptyState message={isSearching ? strNoResults : strNoSections} />
                  )}

                  {/* Infinite scroll loader for sections */}
                  <IntersectionLoader
                    hasNextPage={hasNextSections}
                    isFetching={isFetchingNextSections || isFetchingSections}
                    fetchNextPage={fetchNextSections}
                    ixRef={ixRef}
                    entry={entry}
                    endOfResultsMessage={
                      allSections.length > 0 ? strEndOfResults : undefined
                    }
                    compact
                    hideOnPagesEnd={allSections.length < PAGE_SIZE}
                  />
                </Box>
              )}
            </Box>
          </Box>
        );

      // ===========================================================
      // Step 3: Review & Save
      // ===========================================================
      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: bloxValues.fontWeightMedium }}
            >
              {strReviewRule}
            </Typography>

            <Stack spacing={2}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: bloxValues.tinyFontSizeRem,
                  }}
                >
                  {strRuleType}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: bloxValues.fontWeightMedium }}
                >
                  {ruleTypeOptions.find((o) => o.value === selectedType)?.label}
                </Typography>
              </Box>

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: bloxValues.tinyFontSizeRem,
                  }}
                >
                  {strSelectedValues} ({selectedValues.length})
                </Typography>
                <Stack
                  direction="row"
                  sx={{
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  {selectedValues.map((value) => (
                    <Chip
                      key={value}
                      label={
                        selectedType === 'assetType'
                          ? getAssetTypeLabel(value)
                          : selectedType === 'flags'
                            ? (flagItems?.find((f) => f.key === value)?.label ?? value)
                            : value
                      }
                      size="small"
                      variant="outlined"
                      onDelete={() => onRemoveValue(value)}
                      sx={{
                        '& .MuiChip-deleteIcon': {
                          '&:hover': { color: theme.palette.error.main },
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <TextField
                label={strRuleName}
                value={ruleName}
                onChange={(e) => onRuleNameChange(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                margin="dense"
                helperText={strRuleNameHelper}
                data-testid="rule-name-input"
                slotProps={{
                  input: {
                    sx: { fontSize: bloxValues.smallFontSizeRem },
                  },

                  inputLabel: { shrink: true },
                }}
              />
            </Stack>
          </Box>
        );

      default:
        return null;
    }
  },
);
RuleWizardStep.displayName = 'RuleWizardStep';

// ---------------------------------------------------------
// Rule Wizard Modal
// ---------------------------------------------------------

export const RuleWizardModal = memo(
  ({
    open,
    onClose,
    onSave,
    initialData,
    isEdit = false,
    usedRuleTypes = [],
  }: RuleWizardModalProps) => {
    const { t } = useTranslation();

    const [activeStep, setActiveStep] = useState(0);
    const [selectedType, setSelectedType] = useState<RuleType | null>(
      initialData?.type || null,
    );
    const [selectedValues, setSelectedValues] = useState<string[]>(
      initialData?.values || [],
    );
    const [ruleName, setRuleName] = useState(initialData?.name || '');

    // Translation strings
    const strSave = t('blox.actions.save');
    const strCancel = t('blox.actions.cancel');
    const strNext = t('blox.actions.next');
    const strBack = t('blox.actions.back');
    const strSelected = t('blox.general.selected');
    const strCreateRule = t('blox.notifier.channel.rules.create_rule');
    const strEditRule = t('blox.notifier.channel.rules.edit_rule');
    const strStepChooseType = t('blox.notifier.channel.rules.step_choose_type');
    const strStepSelectCriteria = t('blox.notifier.channel.rules.step_select_criteria');
    const strStepSave = t('blox.notifier.channel.rules.step_save');

    const steps = useMemo(
      () => [strStepChooseType, strStepSelectCriteria, strStepSave],
      [strStepChooseType, strStepSelectCriteria, strStepSave],
    );

    // Reset state when modal opens/closes
    useEffect(() => {
      if (open) {
        if (initialData) {
          setSelectedType(initialData.type);
          setSelectedValues(initialData.values);
          setRuleName(initialData.name || '');
          setActiveStep(isEdit ? 1 : 0);
        } else {
          setSelectedType(null);
          setSelectedValues([]);
          setRuleName('');
          setActiveStep(0);
        }
      }
    }, [open, initialData, isEdit]);

    const handleNext = useCallback(() => {
      if (activeStep === steps.length - 1) {
        if (selectedType && selectedValues.length > 0) {
          onSave({
            name: ruleName || undefined,
            type: selectedType,
            values: selectedValues,
          });
        }
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }, [activeStep, steps.length, selectedType, selectedValues, ruleName, onSave]);

    const handleBack = useCallback(() => {
      setActiveStep((prev) => prev - 1);
    }, []);

    const handleTypeSelect = useCallback((type: RuleType) => {
      setSelectedType(type);
      setSelectedValues([]);
    }, []);

    const handleValueToggle = useCallback((value: string) => {
      setSelectedValues((prev) =>
        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
      );
    }, []);

    const handleAddValue = useCallback((value: string) => {
      setSelectedValues((prev) => (prev.includes(value) ? prev : [...prev, value]));
    }, []);

    const handleRemoveValue = useCallback((value: string) => {
      setSelectedValues((prev) => prev.filter((v) => v !== value));
    }, []);

    const canProceed = useMemo(() => {
      switch (activeStep) {
        case 0:
          return selectedType !== null;
        case 1:
        case 2:
          return selectedValues.length > 0;
        default:
          return false;
      }
    }, [activeStep, selectedType, selectedValues]);

    return (
      <DragModal
        open={open}
        onClose={onClose}
        isDraggable={true}
        isResizable={true}
        initialWidth={600}
        initialHeight={560}
        minWidth={500}
        minHeight={400}
        preventBackdropClickClose={true}
        preventEscapeKeyClose={true}
        header={
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography sx={{ fontWeight: bloxValues.fontWeightMedium }}>
              {isEdit ? strEditRule : strCreateRule}
            </Typography>
            {selectedValues.length > 0 && activeStep > 0 && (
              <Chip
                label={`${selectedValues.length} ${strSelected.toLowerCase()}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: bloxValues.fontWeightMedium }}
              />
            )}
          </Stack>
        }
        body={
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Stepper
              activeStep={activeStep}
              sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    slotProps={{
                      stepIcon: { sx: { fontSize: bloxValues.iconSizeRegular } },
                    }}
                  >
                    <Typography sx={{ fontSize: bloxValues.smallFontSizeRem }}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: activeStep === 1 ? 'hidden' : 'auto',
                px: 2,
                minHeight: 0,
              }}
            >
              <RuleWizardStep
                activeStep={activeStep}
                selectedType={selectedType}
                selectedValues={selectedValues}
                onTypeSelect={handleTypeSelect}
                onValueToggle={handleValueToggle}
                onAddValue={handleAddValue}
                onRemoveValue={handleRemoveValue}
                ruleName={ruleName}
                onRuleNameChange={setRuleName}
                open={open}
                usedRuleTypes={usedRuleTypes}
              />
            </Box>
          </Box>
        }
        footer={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  size="small"
                >
                  {strBack}
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={onClose}
                size="small"
                data-testid="rule-cancel-button"
              >
                {strCancel}
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={handleNext}
                disabled={!canProceed}
                size="small"
                endIcon={activeStep < steps.length - 1 ? <ArrowForwardIcon /> : null}
              >
                {activeStep === steps.length - 1 ? strSave : strNext}
              </Button>
            </Box>
          </Box>
        }
      />
    );
  },
);
RuleWizardModal.displayName = 'RuleWizardModal';
